/**
 * Trust Centre external portal — the "answer-once / reverse-replies" model.
 *
 * READ THIS BEFORE CHANGING THE TIER MATRIX.
 *
 * An organisation pre-answers compliance questions once (the "reverse replies")
 * and gives EXTERNAL parties — a prospect, a customer, a partner, an auditor —
 * gated, read-only access to those answers so they stop sending repeat
 * questionnaires. Those external parties have NO tenant account and never will:
 * they are `TrustExternalUser` rows, a separate identity space from `User`,
 * reachable only through the isolated `/api/trust-portal` surface.
 *
 * WHAT THIS FILE IS
 * The single source of truth for the visibility rules, shared by the API (which
 * ENFORCES them server-side on every request) and the web (which uses them only
 * to decide what to show — never as a security control). Both import from here
 * so the rule a reviewer reads is the rule that runs.
 *
 * THE THREE TIERS, from least to most sensitive:
 *   PUBLIC — anything the organisation would already put on its public Trust
 *            Centre page. Visible to any ACTIVE portal user.
 *   NDA    — shown only once the viewer has accepted the clickwrap NDA
 *            (`ndaAcceptedAt` is set). "NDA = clickwrap" in the product brief.
 *   NAMED  — named-customer-only. The most sensitive band: visible only to a
 *            viewer who has been granted it EXPLICITLY (`canAccessNamed`), AND
 *            who has also accepted the NDA. There is no role that silently
 *            confers it — AUDITOR merely DEFAULTS the grant on at creation.
 *
 * THE THREE ROLES are presets over the tiers, nothing more. A role widens the
 * ceiling of what a viewer MAY see; the NDA state and the explicit NAMED grant
 * decide what they DO see. See `effectiveTiers`.
 */
// ---------------------------------------------------------------------------
//  Enums (stored as String in MySQL; validated from these lists, as every
//  other "enum" in this codebase is — see enums.ts for why).
// ---------------------------------------------------------------------------
const list = (...v) => v;
/** Sensitivity bands for a reverse-reply or a gated document. */
export const TRUST_TIERS = list('PUBLIC', 'NDA', 'NAMED');
/** The access presets an external identity is given. */
export const TRUST_EXTERNAL_ROLES = list('CLIENT', 'PARTNER', 'AUDITOR');
/**
 * Lifecycle of an external identity.
 *   INVITED — created by an admin (or born from an approved request); may sign
 *             in with the initial password. (Phase 1 treats INVITED as able to
 *             sign in; there is no separate "accept invite" step yet.)
 *   ACTIVE  — in good standing.
 *   REVOKED — access withdrawn. Fail-closed: a REVOKED user authenticates to
 *             nothing, immediately, regardless of any token already issued.
 */
export const TRUST_EXTERNAL_STATUSES = list('INVITED', 'ACTIVE', 'REVOKED');
/** Statuses that are permitted to authenticate to the portal. */
export const TRUST_EXTERNAL_SIGNIN_STATUSES = ['INVITED', 'ACTIVE'];
/** Self-service access request lifecycle. */
export const TRUST_ACCESS_REQUEST_STATUSES = list('PENDING', 'APPROVED', 'DENIED');
/** Default lifetime of an external access grant, in days. */
export const TRUST_ACCESS_DEFAULT_DAYS = 90;
/** Bounds on an admin-chosen expiry, so a typo cannot mint a decade-long grant. */
export const TRUST_ACCESS_MIN_DAYS = 1;
export const TRUST_ACCESS_MAX_DAYS = 730;
/**
 * The presets. Deliberately conservative:
 *   * CLIENT and PARTNER reach PUBLIC and, after signing the NDA, NDA. Neither
 *     gets NAMED by default — named-customer material is handed out one viewer
 *     at a time, on purpose.
 *   * AUDITOR reaches everything, because an auditor's whole job is to see the
 *     detail — but still only after the NDA, and the NAMED grant is a stored
 *     flag on their row, not an implicit consequence of the word "AUDITOR".
 */
export const TRUST_ROLE_PRESETS = {
    CLIENT: { tiers: ['PUBLIC', 'NDA'], ndaRequired: true, namedByDefault: false },
    PARTNER: { tiers: ['PUBLIC', 'NDA'], ndaRequired: true, namedByDefault: false },
    AUDITOR: { tiers: ['PUBLIC', 'NDA', 'NAMED'], ndaRequired: true, namedByDefault: true },
};
/** Does this role ever reach NDA-tier content? */
export function roleCanReachNda(role) {
    return TRUST_ROLE_PRESETS[role].tiers.includes('NDA');
}
/** Does this role ever reach NAMED-tier content (with an explicit grant)? */
export function roleCanReachNamed(role) {
    return TRUST_ROLE_PRESETS[role].tiers.includes('NAMED');
}
/**
 * The EXACT set of tiers a viewer may read right now. Fail-closed by
 * construction: it starts from PUBLIC and only ever ADDS a tier when every
 * condition for it is met.
 *
 *   PUBLIC — always (an authenticated, active portal user).
 *   NDA    — the role reaches NDA AND the NDA has been accepted.
 *   NAMED  — the role reaches NAMED, the viewer has the explicit grant, AND the
 *            NDA has been accepted (NAMED content is strictly more sensitive
 *            than NDA content, so it can never be seen without the NDA).
 */
export function effectiveTiers(viewer) {
    const tiers = ['PUBLIC'];
    const ndaAccepted = viewer.ndaAcceptedAt !== null && viewer.ndaAcceptedAt !== undefined;
    if (roleCanReachNda(viewer.role) && ndaAccepted)
        tiers.push('NDA');
    if (roleCanReachNamed(viewer.role) && viewer.canAccessNamed && ndaAccepted)
        tiers.push('NAMED');
    return tiers;
}
/** Whether a viewer may read a single entry of the given tier, right now. */
export function canViewTier(viewer, tier) {
    return effectiveTiers(viewer).includes(tier);
}
/**
 * Whether an NDA acceptance would unlock anything further for this viewer — so
 * the portal can show the clickwrap gate only when it matters.
 */
export function ndaWouldUnlock(role, canAccessNamed) {
    return roleCanReachNda(role) || (roleCanReachNamed(role) && canAccessNamed);
}
/** Has an access grant with this expiry lapsed as of `now`? */
export function isAccessExpired(expiresAt, now = new Date()) {
    if (!expiresAt)
        return false; // null = no expiry set
    return new Date(expiresAt).getTime() <= now.getTime();
}
//# sourceMappingURL=trustPortal.js.map